import { apiClient } from '../utils/apiClient';

interface HeartbeatResponse {
    success: boolean;
    lastActivity: string;
    sessionId: string;
}

interface EndSessionResponse {
    success: boolean;
    duration: number;
    endedAt: string;
    sessionId: string;
}

interface ViewStats {
    totalViews: number;
    uniqueViewers: number;
    activeViewers: number;
    averageDuration: number;
    viewsByDay: Array<{
        date: string;
        views: number;
    }>;
    topViewers: Array<{
        userId: string;
        viewCount: number;
        totalDuration: number;
    }>;
}

// CACHE SIMPLE POUR LES STATS
interface CachedStats {
    data: ViewStats;
    timestamp: number;
    dossierId: string;
}

class StatsCache {
    private cache = new Map<string, CachedStats>();
    private readonly TTL = 5 * 60 * 1000; // 5 minutes

    set(dossierId: string, stats: ViewStats): void {
        this.cache.set(dossierId, {
            data: stats,
            timestamp: Date.now(),
            dossierId
        });
    }

    get(dossierId: string): ViewStats | null {
        const cached = this.cache.get(dossierId);

        if (!cached) {
            return null;
        }

        // Vérifier si le cache n'est pas expiré
        if (Date.now() - cached.timestamp > this.TTL) {
            this.cache.delete(dossierId);
            return null;
        }

        return cached.data;
    }

    clear(): void {
        this.cache.clear();
    }

    // Nettoyer les entrées expirées
    cleanup(): void {
        const now = Date.now();
        // Conversion en Array pour éviter le problème d'itération
        Array.from(this.cache.entries()).forEach(([key, value]) => {
            if (now - value.timestamp > this.TTL) {
                this.cache.delete(key);
            }
        });
    }
}

// Instance globale du cache
const statsCache = new StatsCache();

// Nettoyage automatique du cache toutes les 10 minutes
setInterval(() => {
    statsCache.cleanup();
}, 10 * 60 * 1000);

export const startDossierView = async (dossierId: string): Promise<string | null> => {
    try {
        const response = await apiClient(`/dossiers/${dossierId}`);

        if (!response.ok) {
            throw new Error(`Failed to start view: ${response.status}`);
        }

        const sessionId = response.headers.get('X-Session-Id');

        if (sessionId) {
            return sessionId;
        }

        return null;

    } catch (error) {
        console.error('Erreur lors du démarrage de la vue:', error);
        return null;
    }
};

export const sendHeartbeat = async (dossierId: string, sessionId: string): Promise<boolean> => {
    try {
        const response = await apiClient(`/dossiers/${dossierId}/heartbeat`, {
            method: 'POST',
            body: JSON.stringify({ sessionId }),
        });

        if (!response.ok) {
            console.warn('Heartbeat failed:', response.status);
            return false;
        }

        const data: HeartbeatResponse = await response.json();
        return data.success;

    } catch (error) {
        console.error('Erreur heartbeat:', error);
        return false;
    }
};

export const endDossierView = async (dossierId: string, sessionId: string): Promise<boolean> => {
    try {
        const response = await apiClient(`/dossiers/${dossierId}/end-session`, {
            method: 'POST',
            body: JSON.stringify({ sessionId }),
        });

        if (!response.ok) {
            console.warn('End session failed:', response.status);
            return false;
        }

        const data: EndSessionResponse = await response.json();

        // INVALIDER LE CACHE APRÈS UNE SESSION TERMINÉE
        statsCache.clear();

        return data.success;

    } catch (error) {
        console.error('Erreur fin de session:', error);
        return false;
    }
};

export const getDossierViewStats = async (
    dossierId: string,
    timeframe: string = 'month',
    useCache: boolean = true
): Promise<ViewStats | null> => {
    try {
        // Vérifier le cache d'abord si activé
        if (useCache) {
            const cached = statsCache.get(dossierId);
            if (cached) {
                return cached;
            }
        }

        const response = await apiClient(`/dossiers/${dossierId}/stats/views?timeframe=${timeframe}`);

        if (!response.ok) {
            throw new Error(`Failed to fetch stats: ${response.status}`);
        }

        const data = await response.json();

        const stats: ViewStats = data.stats;

        if (!stats) {
            console.warn('Aucune stats trouvée dans la réponse');
            return null;
        }

        // METTRE EN CACHE LE RÉSULTAT
        if (useCache) {
            statsCache.set(dossierId, stats);
        }

        return stats;

    } catch (error) {
        console.error('Erreur récupération stats:', error);
        return null;
    }
};

export const debugGetDossierViewStats = async (
    dossierId: string,
    timeframe: string = 'month'
): Promise<void> => {
    try {

        const response = await apiClient(`/dossiers/${dossierId}/stats/views?timeframe=${timeframe}`);


        if (!response.ok) {
            const errorText = await response.text();
            console.error('DEBUG - Erreur response:', errorText);
            return;
        }

        await response.json();

    } catch (error) {
        console.error('DEBUG - Fetch error:', error);
    }
};

// FONCTION BATCH POUR RÉCUPÉRER PLUSIEURS STATS EN UNE FOIS
export const getDossiersViewStatsBatch = async (
    dossierIds: string[],
    timeframe: string = 'month'
): Promise<Record<string, ViewStats>> => {
    const results: Record<string, ViewStats> = {};

    // Séparer les IDs selon le cache
    const uncachedIds: string[] = [];

    for (const dossierId of dossierIds) {
        const cached = statsCache.get(dossierId);
        if (cached) {
            results[dossierId] = cached;
        } else {
            uncachedIds.push(dossierId);
        }
    }

    // Si tous les résultats sont en cache, retourner directement
    if (uncachedIds.length === 0) {
        return results;
    }

    // Récupérer les stats manquantes en parallèle (max 5 simultanées)
    const chunks = [];
    for (let i = 0; i < uncachedIds.length; i += 5) {
        chunks.push(uncachedIds.slice(i, i + 5));
    }

    for (const chunk of chunks) {
        const promises = chunk.map(async (dossierId) => {
            const stats = await getDossierViewStats(dossierId, timeframe, true);
            if (stats) {
                results[dossierId] = stats;
            }
            return { dossierId, stats };
        });

        await Promise.allSettled(promises);
    }

    return results;
};

export const getActiveViewers = async (dossierId: string) => {
    try {
        const response = await apiClient(`/dossiers/${dossierId}/stats/active`);

        if (!response.ok) {
            throw new Error(`Failed to fetch active viewers: ${response.status}`);
        }

        return await response.json();

    } catch (error) {
        console.error('Erreur récupération viewers actifs:', error);
        return null;
    }
};

export const getDossierViewCount = async (dossierId: string): Promise<number> => {
    try {
        // D'abord vérifier le cache
        const cached = statsCache.get(dossierId);
        if (cached) {
            return cached.totalViews;
        }

        // Sinon, récupérer les stats complètes
        const stats = await getDossierViewStats(dossierId, 'month', true);
        return stats?.totalViews || 0;

    } catch (error) {
        console.error('Erreur récupération count:', error);
        return 0;
    }
};

// FONCTION UTILITAIRE POUR RAFRAÎCHIR LE CACHE
export const refreshStatsCache = (dossierId?: string): void => {
    if (dossierId) {
        statsCache.get(dossierId);
    } else {
        statsCache.clear();
    }
};

export interface AggregateStats {
    totalViews: number;
    uniqueViewers: number;
    followerCount: number;
}

export const getMyDossiersAggregateStats = async (): Promise<AggregateStats | null> => {
    try {
        const response = await apiClient('/dossiers/stats/my-aggregate');

        if (!response.ok) return null;

        const data = await response.json();
        return data.stats || null;
    } catch {
        return null;
    }
};

// Export du cache pour debug (development only)
if (process.env.NODE_ENV === 'development') {
    (window as any).dossierStatsCache = statsCache;
    (window as any).debugDossierStats = debugGetDossierViewStats;
}
